/**
 * Neshan Reverse Geocoding — turns a lat/lng into a human-readable Persian
 * address via https://api.neshan.org/v5/reverse.
 *
 * NOTE: this requires a Neshan *service* key (service.*), configured in
 * env-config.js as `Neshan.ServiceKey` — NOT the web/map key used to render
 * tiles. Called directly from the browser (not through `netCall`, which injects
 * BFF-only headers/redirect logic meant for same-origin calls).
 */
import { EnvConfig } from "environment/environment-model";

export type NeshanReverseResult = {
  status: string;
  formatted_address: string;
  route_name?: string;
  route_type?: string;
  neighbourhood?: string;
  city?: string;
  state?: string;
  place?: string | null;
  municipality_zone?: string;
  county?: string;
  district?: string;
  village?: string | null;
};

const getServiceKey = (): string | undefined =>
  ((window as any).globalEnvConfig as EnvConfig | undefined)?.Neshan
    ?.ServiceKey;

export async function reverseGeocode(
  lat: number,
  lng: number,
  signal?: AbortSignal
): Promise<NeshanReverseResult | null> {
  const key = getServiceKey();
  if (!key || key.includes("REPLACE_WITH_YOUR")) {
    console.warn("Neshan service key is not configured (Neshan.ServiceKey).");
    return null;
  }

  try {
    const res = await fetch(
      `https://api.neshan.org/v5/reverse?lat=${lat}&lng=${lng}`,
      { headers: { "Api-Key": key }, signal }
    );
    if (!res.ok) return null;
    const data = (await res.json()) as NeshanReverseResult;
    return data?.status === "OK" ? data : null;
  } catch (err) {
    // Aborted requests (stale picks) land here — swallow quietly.
    if ((err as any)?.name === "AbortError") return null;
    console.warn("Neshan reverse geocoding failed:", err);
    return null;
  }
}
